<template>
  <Toast
    position="bottom-center"
    class="itemlist-toast-wrapper"
    :style="{ bottom: '4.8rem', left: '51.5%' }"
    group="rowactionmenubar"
    :pt="{
      closeButton: {
          autofocus: false,
          class: 'itemlist-toast-close-button',
          style: {
            width: '2.4rem',
            height: '2.4rem',
            color: '#ffffff',
          },
      }
    }"
  >
    <template #message>
      <div class="itemlist-toast" v-focustrap :autofocus="false">
      <div class="itemlist-toast itemlist-toast-multiple-item-width'">
        <IconButton
          tabindex="0"
          :icon="CheckboxSemiIcon"
          :alt="'Deselect'"
          v-tooltip.top="'Deselect'"
          xsmall
          @click="deselectAllItems"
          aria-haspopup="true"
          aria-controls="menus"
          ariaLabel="Deselect all items"
          :enterPress="() => deselectAllItems"
          class="itemlist-toast-deselect"
        />
        <div class="itemlist-toast-text">
          <strong>{{ selection.length }} selected</strong>
        </div>
        <div aria-hidden="true" class="itemlist-toast-separator" />
        <button
          ref="renameButton"
          v-if="!multipleItemsSelected"
          @click="renameItem"
          @keydown="($event) => {
            if ($event.key === 'Enter') {
              $event.preventDefault();
              renameItem();
            }
          }"
          class="itemlist-toast-button itemlist-toast-button-first"
          :disabled="multipleItemsSelected"
          aria-label="Rename"
        >
          <img role="presentation" :src="RenameIcon" alt="RenameIcon" />
          Rename
        </button>
        <button
          ref="moveButton"
          v-if="itemType !== 'folder'"
          @click="moveItem"
          @keydown="($event) => {
            if ($event.key === 'Enter') {
              $event.preventDefault();
              moveItem();
            }
          }"
          class="itemlist-toast-button"
          :class="
            selection.length !== 1 ? 'itemlist-toast-button-first' : ''
          "
          :disabled="hasMixedTypes"
          aria-label="Move"
        >
          <img :src="MoveIcon" alt="MoveIcon" />
          Move
        </button>
        <button
          ref="archiveButton"
          @click="archiveItem"
          @keydown="($event) => {
            if ($event.key === 'Enter') {
              $event.preventDefault();
              archiveItem();
            }
          }"
          class="itemlist-toast-button"
          :class="
            selection.length === 1 ? 'itemlist-toast-button-last' : ''
          "
          :disabled="hasMixedTypes"
          aria-label="Archive"
        >
          <img :src="ArchiveIcon" alt="ArchiveIcon" />
          Archive
        </button>
        <IconButton
          v-if="selection.length === 1"
          class="itemlist-toast-deselect itemlist-toast-left-seperate"
          :icon="ThreeDotsWhiteIcon"
          :alt="'Settings'"
          v-tooltip.top="'Settings'"
          xsmall
          @click="(e: Event) => props.toggleSessionMenu(e, selection[0])"
          ariaLabel="Open session menu"
          :enterPress="(e: Event) => props.toggleSessionMenu(e, selection[0])"
          aria-haspopup="true"
          aria-controls="menus"
        />
      </div>
      </div>
    </template>
  </Toast>
  <Toast
    position="top-center"
    group="notification"
    class="itemlist-notification-toast-wrapper"
  ></Toast>
</template>

<script setup lang="ts">
import { watch, ref, computed, onMounted, onUnmounted } from "vue";
import { useToast } from "primevue/usetoast";
import IconButton from "../IconButton.vue";
import { SessionWithOrdersAndType } from "../../types/session-interfaces";
import { FolderWithSessions } from "../../types/folder-interfaces";
import { useStore } from "vuex";

const ThreeDotsWhiteIcon =
  require("../../assets/images/ThreeDotsWhiteIcon.svg") as string;
const CheckboxSemiIcon =
  require("../../assets/images/CheckmarkInputSemi.svg") as string;
const MoveIcon = require("../../assets/images/SmallDarkMove.svg") as string;
const ArchiveIcon =
  require("../../assets/images/SmallDarkArchive.svg") as string;
const RenameIcon = require("../../assets/images/SmallDarkRename.svg") as string;

const props = defineProps<{
  toggleSessionMenu: Function;
  visible: boolean;
}>();

const emit = defineEmits(["renameItem", "moveItem"]);

const toast = useToast(), 
      store = useStore(),
      hasMixedTypes = ref<boolean>(false),
      multipleItemsSelected = ref<boolean>(false),
      itemType = ref<string>(),
      renameButton = ref<HTMLButtonElement | null>(null),
      archiveButton = ref<HTMLButtonElement | null>(null),
      moveButton = ref<HTMLButtonElement | null>(null);

const selection = computed({
        get: () =>  store.getters['itemList/getSelectedItems'],
        set: (val) => store.dispatch('itemList/updateSelection', val),
      });

watch(selection, ( newValue ) => {
  const firstType = newValue[0]?.type;
  itemType.value = firstType;
  
  if (newValue && newValue.length === 0) {
      multipleItemsSelected.value = false;
    } else if (newValue && newValue.length > 1) {
      multipleItemsSelected.value = true;

        // Check if all items have the same type
      hasMixedTypes.value = newValue.some((item: SessionWithOrdersAndType | FolderWithSessions ) => item.type !== firstType);

        if (hasMixedTypes.value) {
          setTimeout(() => {
            toast.add({
              group: "notification",
              summary: "You can’t select both folders and sessions.",
              life: 3000,
            });
          }, 20);
        }
    }
    else {
      multipleItemsSelected.value = false;
      hasMixedTypes.value = false;
  }
});

watch(() => props.visible, (newValue) => {
  if (newValue === true) {
    setTimeout(() => {
          toast.add({
            detail: selection,
            group: "rowactionmenubar",
          });
        }, 20);
  } else {
    toast.removeGroup("rowactionmenubar");
  }
})

function renameItem() {
  emit("renameItem");
}

async function archiveItem() {
  const plural = selection.value.length > 1;
  await Promise.all(
  selection.value.map((item: 
  SessionWithOrdersAndType | FolderWithSessions) =>
    store.dispatch('itemList/archiveItem', item.id)
  ));
  await store.dispatch('itemList/clearSelection');

  toast.add({
    severity: "secondary",
    summary: `Item${plural ? 's' : ''} archived successfully.`,
    life: 3000,
    group: "notification",
  });
}

function moveItem() {
  emit("moveItem");
}

function deselectAllItems() {
  store.commit("itemList/clearSelection");
}

function handleKeyDown(event: KeyboardEvent) {
  switch (event.key) {
    case "r":
      renameButton.value?.focus();
      break;
    case "a":
      archiveButton.value?.focus();
      break;
    case "m":
      moveButton.value?.focus();
      break;
    case "Escape":
      // eslint-disable-next-line no-case-declarations
      const activeElement = document.activeElement as HTMLElement;
      if (activeElement) {
        activeElement.blur();
      }
      break;
  }
}

onMounted(() => {
  window.addEventListener("keydown", handleKeyDown)
})

onUnmounted(() => {
  window.removeEventListener("keydown", handleKeyDown)
})
</script>

<style lang="scss">
.itemlist-threedoticon-svg-icon {
  background: url("../../assets/images/ThreeDotsWhiteIcon.svg");
  height: 2rem;
  width: 2rem;
}

.itemlist-upload-svg-icon {
  background: url("../../assets/images/Upload-black.svg");
  height: 2rem;
  width: 2rem;
}

.itemlist-popupmenu {
  height: 1.6rem;
  width: 1.6rem;
}

.itemlist-toast-wrapper {
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: row;
  min-width: max-content;

  .itemlist-toast {
    display: flex;
    flex-direction: row;
    justify-content: center;
    align-items: center;

    button {
      display: block;
    }

    .itemlist-toast-button {
      display: flex;
      justify-content: center;
      align-items: center;
      margin: 0 0.8rem 0 0.8rem;
      padding: 0.8rem 1.6rem;
      font-size: 1.4rem;

      img {
        width: 1.6rem;
        height: 1.6rem;
      }
      &:disabled {
        // background-color: #e0e0e0;
        // color: #a0a0a0;
        cursor: not-allowed;
        pointer-events: none;

        img {
          opacity: 0.5;
        }
      }
    }

    .itemlist-toast-button-first {
      margin-left: 1.6rem;
    }

    .itemlist-toast-button-last {
      margin-right: 1.6rem;
    }

    .itemlist-toast-deselect {
      display: block;
    }

    .itemlist-toast-left-seperate {
      display: block;
      border-left: 0.1rem solid #1919194c;
      border-radius: 0;
      padding-left: 1.2rem;
    }

    .itemlist-toast-text {
      padding: 0 1.6rem 0 1.6rem;
      height: 2.4rem;
      border-right: 0.1rem solid #1919194c;
      display: flex;
      justify-content: center;
      align-items: center;
    }
  }

  .p-toast-message-content {
    display: flex;
    background: map-get($colours, accent-purple-3);
    justify-content: center;
    align-items: center;
    flex-direction: row;
  }

  .p-toast-top-right {
    top: 4.8rem !important;
  }
}

.itemlist-notification-toast-wrapper {
  margin: 0;
  padding: 0;

  .p-toast-message-content {
    margin: 0;
    padding: 0;
    background: map-get($colours, accent-purple-3);
    font-size: 1.4rem;
  }

  .p-toast-message-icon {
    margin: 0;
    padding: 0;
    width: 0;
    height: 0;
  }
}

.itemlist-toast-single-item-width {
  width: 56.5rem;
}

.itemlist-toast-multiple-item-width {
  width: 39rem;
}

.p-toast {
  opacity: 1;
  .p-toast-message {
    // TODO: rewrite so that all toast styling is scoped properly
    border-radius: 1.2rem !important;
    .p-toast-message-content {
      border-radius: 1.2rem !important;
    }
  }
}
</style>
